import React, { useCallback, useState } from 'react'
import { BallotBoxIcon } from '@/atoms/Icons/BallotBoxIcon'
import { NewsIcon } from '@/atoms/Icons/NewsIcon'
import { UsersThreeIcon } from '@/atoms/Icons/UsersThreeIcon'
import { CaptionMD, HeadingMD, ParagraphSM } from '@/atoms/Text'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { TorchSubmitButton } from '../TorchSubmitButton'
import { AccordionSection } from './AccordionSection'
import { RuleSubsection } from './RuleSubsection'

export const SubmissionRulesSection: React.FC = () => {
  const { track } = useSafeAnalytics()
  const { t } = useTranslate('create')
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    general: true,
    kids: true,
    unbiased: true,
  })

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const allSubmissionsRules = [
    t('noNudity', 'No nudity'),
    t('noSexualActs', 'No sexual acts shown on screen'),
    t('noFWord', 'No use of the word F**k'),
    t('noLordsName', "No use of the Lord's name in vain"),
    t('noFilmmakerExplainer', 'No filmmaker explainer videos or speeches'),
    t('noAwardsReference', 'No reference to awards or other productions'),
  ]

  const episodeRules = [
    t(
      'noCreditsWebsites',
      'No credits, websites, QR codes, company logos, company names; however, Documentary/Reality participant names are allowed to be shown',
    ),
  ]

  const torchRules = [
    t('animatedTorches2Plus', 'Animated Torches are 2+ minutes'),
    t('otherTorches5Plus', 'Other Torches are 5+ minutes'),
    t('torchesLessThan20', 'Should be less than 20 minutes'),
    t('torchContinuousSequence', 'Must be a continuous sequence (or 2 to 3 scenes put together)'),
    t('trailersNotAcceptable', 'No trailers or sizzles'),
    t('torchGenreFormat', 'The genre and format must represent the final production'),
    t(
      'noCreditsWebsites',
      'No credits, websites, QR codes, company logos, company names; however, Documentary/Reality participant names are allowed to be shown',
    ),
  ]

  const teenagerRules = [t('allOfTheAbove', 'All of the above'), t('noCWord', 'Plus no use of the word C**t')]

  const youngerKidsRules = [t('allOfTheAbove', 'All of the above'), t('noSwearing', 'Plus no swearing of any kind')]

  const unbiasedRules = [
    t(
      'randomQueue',
      "Once your submission is approved, we place it in a random queue with other submissions to keep the Guild score fair and accurate. While it's in voting, it won't be searchable.",
    ),
    t(
      'confidentialScoring',
      'To maintain the integrity of the voting process, the scoring algorithm, including the total number of voters on each submission, will remain confidential.',
    ),
  ]

  const handleGetStartedClicked = useCallback(() => {
    track('Guild Submit Project Clicked')
  }, [track])

  return (
    <section
      className="w-full py-16 md:py-24"
      style={{
        backgroundImage: 'linear-gradient(130deg, #232323 0%, #141414 100%)',
      }}
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-12 lg:px-[120px]">
        <HeadingMD weight="bold" color="white" className="md:photon-heading-xl lg:photon-display mb-4 text-center">
          {t('rulesForSubmissions', 'Rules For Submissions')}
        </HeadingMD>

        <ParagraphSM weight="normal" color="core-gray-400" className="mx-auto mb-12 max-w-2xl text-center">
          {t(
            'rulesDescription',
            'These rules are in place to ensure an unbiased voting process and to meet the content expectations of our Guild voters.',
          )}
        </ParagraphSM>

        <div className="mb-12 space-y-6">
          <AccordionSection
            title={t('generalRules', 'General Rules')}
            icon={<NewsIcon size={48} color="white" />}
            isOpen={openSections.general ?? false}
            onToggle={() => toggleSection('general')}
          >
            <RuleSubsection title={t('allSubmissions', 'All Submissions')} rules={allSubmissionsRules} />
            <RuleSubsection title={t('episodes', 'Episodes')} rules={episodeRules} />
            <RuleSubsection title={t('torches', 'Torches')} rules={torchRules} />
          </AccordionSection>

          <AccordionSection
            title={t('submissionsForKids', 'Submissions for Kids')}
            icon={<UsersThreeIcon size={48} color="white" />}
            isOpen={openSections.kids ?? false}
            onToggle={() => toggleSection('kids')}
          >
            <RuleSubsection title={t('teenagers', 'Teenagers')} rules={teenagerRules} />
            <RuleSubsection title={t('youngerKids', 'Younger Kids')} rules={youngerKidsRules} />
          </AccordionSection>

          <AccordionSection
            title={t('keepingSubmissionsUnbiased', 'Keeping Submissions Unbiased')}
            icon={<BallotBoxIcon size={48} color="white" />}
            isOpen={openSections.unbiased ?? false}
            onToggle={() => toggleSection('unbiased')}
          >
            <RuleSubsection title={''} rules={unbiasedRules} />
          </AccordionSection>
        </div>

        <div className="flex flex-col items-center gap-4">
          <TorchSubmitButton handleSubmitClicked={handleGetStartedClicked} isDark={true} />
          <CaptionMD weight="normal" color="core-gray-500">
            {t('applicationFee', '$250 application fee')}
          </CaptionMD>
        </div>
      </div>
    </section>
  )
}
